import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ComplianceNotice from '../components/ComplianceNotice';
import { 
  DocumentIcon, 
  PlusIcon, 
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  FolderIcon,
  CalendarIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface Document {
  id: string;
  title: string;
  uploadDate: string;
  fileType: string;
  url: string;
  category: string | null;
}

const Documents: React.FC = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [newDocument, setNewDocument] = useState({
    title: '',
    category: 'bylaws',
    description: '',
    file: null as File | null
  });

  const fetchDocuments = useCallback(async () => {
    if (!user?.propertyGroupId) return;

    setLoading(true);
    try {
      const response = await axios.get<Document[]>('/api/documents', {
        params: { propertyGroupId: user.propertyGroupId },
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setDocuments(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
      setError('Unable to load documents. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.propertyGroupId, user?.token]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getFileTypeIcon = (fileType: string) => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return '📄';
      case 'xlsx':
      case 'xls':
        return '📊';
      case 'docx':
      case 'doc':
        return '📝';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return '🖼️';
      default:
        return '📁';
    }
  };

  const getFileTypeColor = (fileType: string) => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return 'text-red-400 bg-red-500/20';
      case 'xlsx':
      case 'xls':
        return 'text-green-400 bg-green-500/20';
      case 'docx':
      case 'doc':
        return 'text-blue-400 bg-blue-500/20';
      default:
        return 'text-cyan-300 bg-cyan-400/20';
    }
  };

  const categories = [
    { id: 'all', name: 'All Documents', count: documents.length },
    { id: 'bylaws', name: 'Bylaws & Rules', count: documents.filter(d => d.category === 'bylaws').length },
    { id: 'financial', name: 'Financial Reports', count: documents.filter(d => d.category === 'financial').length },
    { id: 'maintenance', name: 'Maintenance', count: documents.filter(d => d.category === 'maintenance').length },
    { id: 'meeting', name: 'Meeting Minutes', count: documents.filter(d => d.category === 'meeting').length },
    { id: 'forms', name: 'Forms', count: documents.filter(d => d.category === 'forms').length }
  ];

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const recentUploads = documents.filter(doc => {
    const uploaded = new Date(doc.uploadDate).getTime();
    if (Number.isNaN(uploaded)) return false;
    return Date.now() - uploaded <= 30 * 24 * 60 * 60 * 1000;
  }).length;

  const handleDownload = (doc: Document) => {
    // Simulate download
    const link = document.createElement('a');
    link.href = doc.url;
    link.download = `${doc.title}.${doc.fileType.toLowerCase()}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Show success message
    alert(`📥 "${doc.title}" download started!`);
  };

  const handleDelete = async (docId: string, docTitle: string) => {
    if (!window.confirm(`Are you sure you want to delete "${docTitle}"?`)) return;
    
    try {
      await axios.delete(`/api/documents/${docId}`, {
        headers: { Authorization: `Bearer ${user?.token}` }
      });
      
      fetchDocuments();
      alert(`🗑️ "${docTitle}" has been deleted.`);
    } catch (error) {
      console.error('Error deleting document:', error);
      alert(`Failed to delete "${docTitle}". Please try again.`);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDocument.file) {
      alert('Please select a file to upload.');
      return;
    }
    
    if (!user?.propertyGroupId) {
      alert('No property group assigned. Please contact support.');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', newDocument.file);
      formData.append('title', newDocument.title);
      formData.append('category', newDocument.category);
      formData.append('description', newDocument.description);
      formData.append('propertyGroupId', user.propertyGroupId);
      
      await axios.post('/api/documents', formData, {
        headers: { 
          Authorization: `Bearer ${user.token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      fetchDocuments();
      setNewDocument({ title: '', category: 'bylaws', description: '', file: null });
      setShowUploadModal(false);
      alert(`📤 "${newDocument.title}" uploaded successfully!`);
    } catch (error) {
      console.error('Error uploading document:', error);
      alert(`Failed to upload "${newDocument.title}". Please try again.`);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setNewDocument(prev => ({
        ...prev,
        file,
        title: prev.title || file.name.split('.')[0]
      }));
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {user?.role === 'RESIDENT' ? 'Community Documents' : 'Document Management'}
          </h1>
          <p className="text-white/60">
            {user?.role === 'RESIDENT' 
              ? 'Access important property documents and forms'
              : 'Manage and organize all property documents'
            }
          </p>
        </div>
        {user?.role === 'ADMIN' && (
          <button 
            onClick={() => setShowUploadModal(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Upload Document</span>
          </button>
        )}
      </div>

      <ComplianceNotice />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="floating-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">Total Documents</p>
              <p className="text-2xl font-bold text-white mt-1">{documents.length}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#22d3ee]/20 to-[#38bdf8]/20 flex items-center justify-center">
              <DocumentIcon className="w-6 h-6 text-[#22d3ee]" />
            </div>
          </div>
        </div>

        <div className="floating-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">Recent Uploads (30d)</p>
              <p className="text-2xl font-bold text-green-400 mt-1">{recentUploads}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-green-500/20 to-emerald-500/20 flex items-center justify-center">
              <ArrowDownTrayIcon className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </div>

        <div className="floating-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">Categories</p>
              <p className="text-2xl font-bold text-blue-400 mt-1">{categories.length - 1}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500/20 to-cyan-400/20 flex items-center justify-center">
              <FolderIcon className="w-6 h-6 text-blue-400" />
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="floating-card border border-red-500/30 bg-red-500/10">
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      {/* Search and Filters */}
      <div className="floating-card">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" />
            <input 
              type="text" 
              placeholder="Search documents..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field w-full pl-10"
            />
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`
                  px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200
                  ${selectedCategory === category.id 
                    ? 'bg-[#22d3ee] text-white shadow-lg shadow-[#22d3ee]/25' 
                    : 'bg-white/10 text-white/80 hover:bg-white/20'}
                `}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDocuments.map(document => (
          <div 
            key={document.id} 
            className="floating-card group hover:scale-[1.02] transition-all duration-200"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="text-2xl">{getFileTypeIcon(document.fileType)}</div>
                <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getFileTypeColor(document.fileType)}`}>
                  {document.fileType}
                </span>
              </div>
              {user?.role === 'ADMIN' && (
                <button 
                  onClick={() => handleDelete(document.id, document.title)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-300"
                  aria-label={`Delete document ${document.title}`}
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
            </div>
            
            <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#22d3ee] transition-colors">
              {document.title}
            </h3>
            
            <div className="flex items-center text-xs text-white/40 mb-4">
              <CalendarIcon className="w-4 h-4 mr-2" />
              Uploaded: {formatDate(document.uploadDate)}
            </div>

            <button 
              onClick={() => handleDownload(document)}
              className="w-full btn-primary flex items-center justify-center space-x-2"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              <span>Download</span>
            </button>
          </div>
        ))}
      </div>

      {loading && documents.length === 0 && !error && (
        <div className="floating-card text-center py-12">
          <DocumentIcon className="w-16 h-16 text-white/20 mx-auto mb-4 animate-pulse" />
          <h3 className="text-xl font-semibold text-white/60 mb-2">Loading documents…</h3>
        </div>
      )}

      {!loading && filteredDocuments.length === 0 && (
        <div className="floating-card text-center py-12">
          <DocumentIcon className="w-16 h-16 text-white/20 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white/60 mb-2">No documents found</h3>
          <p className="text-white/40">
            {error
              ? 'We couldn\'t load documents right now.'
              : searchTerm
                ? 'Try adjusting your search terms'
                : 'No documents available in this category'}
          </p>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-card w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Upload Document</h2>
              <button 
                onClick={() => setShowUploadModal(false)}
                className="text-white/60 hover:text-white transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">Document Title</label>
                <input
                  type="text"
                  value={newDocument.title}
                  onChange={(e) => setNewDocument({...newDocument, title: e.target.value})}
                  className="input-field w-full"
                  placeholder="Enter document title"
                  required
                />
              </div>

              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">Category</label>
                <select
                  value={newDocument.category}
                  onChange={(e) => setNewDocument({...newDocument, category: e.target.value})}
                  className="input-field w-full"
                >
                  <option value="bylaws">Bylaws & Rules</option>
                  <option value="financial">Financial Reports</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="meeting">Meeting Minutes</option>
                  <option value="forms">Forms</option>
                </select>
              </div>

              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">Description (Optional)</label>
                <textarea
                  value={newDocument.description}
                  onChange={(e) => setNewDocument({...newDocument, description: e.target.value})}
                  className="input-field w-full h-20 resize-none"
                  placeholder="Brief description of the document..."
                />
              </div>

              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">File</label>
                <input
                  type="file"
                  onChange={handleFileSelect}
                  className="input-field w-full"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  required
                />
                {newDocument.file && (
                  <p className="text-xs text-white/60 mt-2">
                    Selected: {newDocument.file.name} ({(newDocument.file.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1"
                >
                  Upload
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents; 